package xim.poc.game.configuration.v0.tactics

import xim.poc.ActionTargetFilter
import xim.poc.ActorContext
import xim.poc.ActorManager
import xim.poc.game.ActorState
import xim.poc.game.AttackContext
import xim.poc.game.AttackContexts
import xim.resource.DatId
import kotlin.time.Duration.Companion.milliseconds
import kotlin.time.Duration.Companion.seconds

interface TacticsPositionSkill {

    fun isValid(match: TacticsMatchInstance, source: ActorState, target: ActorState): Boolean

    fun apply(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn, source: ActorState, target: ActorState)

    fun displayName(): String

    fun description(): String

    fun tpCost(): Int = 200

}

object TacticsPositionSwap: TacticsPositionSkill {

    override fun isValid(match: TacticsMatchInstance, source: ActorState, target: ActorState): Boolean {
        if (ActionTargetFilter.areEnemies(source, target)) { return false }

        val sourcePosition = match.getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return false }

        val targetPosition = match.getUnitPosition(target)
        if (targetPosition !is PanelPosition) { return false }

        return sourcePosition.distance(targetPosition) == 1
    }

    override fun apply(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn, source: ActorState, target: ActorState) {
        if (!isValid(match, source, target)) { return }

        val sourcePosition = match.getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return }

        val targetPosition = match.getUnitPosition(target)
        if (targetPosition !is PanelPosition) { return }

        source.setCursorPosition(targetPosition)
        source.setMovementCommand(targetPosition.toWorldSpace())

        target.setCursorPosition(sourcePosition)
        target.setMovementCommand(sourcePosition.toWorldSpace())
    }

    override fun displayName(): String {
        return "Swap"
    }

    override fun description(): String {
        return "Swap positions with an adjacent ally."
    }

}

object TacticsPositionPush: TacticsPositionSkill {

    override fun isValid(match: TacticsMatchInstance, source: ActorState, target: ActorState): Boolean {
        return getDestination(match, source, target) != null
    }

    override fun apply(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn, source: ActorState, target: ActorState) {
        val targetDestination = getDestination(match, source, target) ?: return
        val sourceDestination = turn.actionBuilder.destination ?: return

        source.setCursorPosition(sourceDestination)
        source.setMovementCommand(sourceDestination.toWorldSpace())

        val context = AttackContext.from(source, target)
        val lock = turn.lock()
        AttackContext.compose(context) {
            lock.locked = false
            turn.onSkillAnimationComplete(endWait = 500.milliseconds)

            target.frozenTimer.reset(0.5.seconds)
            target.setCursorPosition(targetDestination)
            target.setMovementCommand(targetDestination.toWorldSpace())
        }

        ActorManager[source.id]?.enqueueModelRoutine(
            routineId = DatId("ati0"),
            actorContext = ActorContext(
                originalActor = source.id,
                primaryTargetId = target.id,
                attackContexts = AttackContexts.single(target.id, context)
            ))
    }

    override fun displayName(): String {
        return "Push"
    }

    override fun description(): String {
        return "Push an adjacent ally."
    }

    private fun getDestination(match: TacticsMatchInstance, source: ActorState, target: ActorState): PanelPosition? {
        if (ActionTargetFilter.areEnemies(source, target)) { return null }

        val sourcePosition = match.getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return null }

        val targetPosition = match.getUnitPosition(target)
        if (targetPosition !is PanelPosition) { return null }

        if (sourcePosition.distance(targetPosition) != 1) { return null }

        val dx = targetPosition.x - sourcePosition.x
        val dy = targetPosition.y - sourcePosition.y

        val destination = PanelPosition.maybe(targetPosition.x + dx, targetPosition.y + dy) ?: return null
        if (match.getUnit(destination) != null) { return null }

        return destination
    }

}

object TacticsPositionPull: TacticsPositionSkill {

    override fun isValid(match: TacticsMatchInstance, source: ActorState, target: ActorState): Boolean {
        return getDestination(match, source, target) != null
    }

    override fun apply(match: TacticsMatchInstance, turn: TacticsMatchUnitTurn, source: ActorState, target: ActorState) {
        val sourceDestination = getDestination(match, source, target) ?: return
        val targetDestination = turn.actionBuilder.destination ?: return

        source.setCursorPosition(sourceDestination)
        source.setMovementCommand(sourceDestination.toWorldSpace())

        target.setCursorPosition(targetDestination)
        target.setMovementCommand(targetDestination.toWorldSpace())
    }

    override fun displayName(): String {
        return "Pull"
    }

    override fun description(): String {
        return "Pulls an adjacent ally."
    }

    private fun getDestination(match: TacticsMatchInstance, source: ActorState, target: ActorState): PanelPosition? {
        if (ActionTargetFilter.areEnemies(source, target)) { return null }

        val sourcePosition = match.getUnitPosition(source)
        if (sourcePosition !is PanelPosition) { return null }

        val targetPosition = match.getUnitPosition(target)
        if (targetPosition !is PanelPosition) { return null }

        if (sourcePosition.distance(targetPosition) != 1) { return null }

        val dx = sourcePosition.x - targetPosition.x
        val dy = sourcePosition.y - targetPosition.y

        val destination = PanelPosition.maybe(sourcePosition.x + dx, sourcePosition.y + dy) ?: return null
        if (match.getUnit(destination) != null) { return null }

        return destination
    }

}